import firebase from "firebase/compat/app"; // Import compat for Auth providers
import { db, auth, getSecondaryApp } from './firebase'; // Removed 'storage' import
import { 
  collection, getDocs, doc, setDoc, getDoc, deleteDoc, query, where, limit, writeBatch, updateDoc, runTransaction, onSnapshot, orderBy, increment
} from "firebase/firestore";

import { User, UserRole, SurveyData, CardTemplate, DynamicQuestion, SurveySection, BusJourney, BusStudent, StaffMember, AttendanceRecord } from '../types';
import { INITIAL_QUESTIONS, INITIAL_SECTIONS } from '../constants';

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid || null,
      email: auth.currentUser?.email || null,
      emailVerified: auth.currentUser?.emailVerified || null,
      isAnonymous: auth.currentUser?.isAnonymous || null,
      tenantId: auth.currentUser?.tenantId || null,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

const SURVEYS_COLLECTION = 'surveys';
const USERS_COLLECTION = 'users';
const SETTINGS_COLLECTION = 'settings';

// Helper to construct email from username
export const getEmail = (username: string) => {
  const clean = username.trim();
  if (clean.includes('@')) return clean;
  return `${clean.toLowerCase().replace(/\s/g, '')}@mahallu.app`;
};

export const initStorage = async () => {
  // CRITICAL: If no user is logged in, Firestore rules will likely block this.
  // We check for currentUser. If null, we skip initialization.
  // It will be called again after login in App.tsx.
  if (!auth.currentUser) {
    console.log("Skipping storage initialization (Not logged in)");
    return;
  }

  try {
    // 1. Check Questions
    const questionsDoc = await getDoc(doc(db, SETTINGS_COLLECTION, 'questions'));
    if (!questionsDoc.exists()) {
      await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: INITIAL_QUESTIONS });
    } else {
      let currentList = questionsDoc.data().list as DynamicQuestion[];
      let needsUpdate = false;
      if (!currentList.find(q => q.id === 'isIICMember')) {
        currentList.push({ id: 'isIICMember', label: 'Are you IIC member?', type: 'yesno', section: 'sectionA', required: true });
        needsUpdate = true;
      }
      if (!currentList.find(q => q.id === 'parentName')) {
        currentList.push({ id: 'parentName', label: 'രക്ഷിതാവിന്റെ പേര് (Parent/Guardian Name)', type: 'text', section: 'sectionA', required: true });
        needsUpdate = true;
      }
      if (needsUpdate) {
        await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: currentList });
      }
    }

    // 2. Check Sections
    const sectionsDoc = await getDoc(doc(db, SETTINGS_COLLECTION, 'sections'));
    if (!sectionsDoc.exists()) {
      await setDoc(doc(db, SETTINGS_COLLECTION, 'sections'), { list: INITIAL_SECTIONS });
    }
  } catch (error: any) {
    // Gracefully handle permission errors if rules are strict
    if (error.code === 'permission-denied') {
      console.warn("Storage initialization skipped due to permissions. (Normal if basic user)");
    } else {
      console.warn("Storage initialization warning:", error);
    }
  }
};

// --- Authentication ---

export const createUserProfile = async (uid: string, username: string, name: string, role: UserRole): Promise<User> => {
  const user: User = {
    id: uid,
    username,
    name,
    role,
    visibleSections: ['sectionA', 'sectionB', 'sectionC', 'sectionD'],
    isActive: true
  };
  await setDoc(doc(db, USERS_COLLECTION, uid), user);
  return user;
};

export const registerMasterAdmin = async (name: string, username: string, password: string): Promise<User> => {
  const email = getEmail(username);
  try {
    const userCredential = await auth.createUserWithEmailAndPassword(email, password);
    const uid = userCredential.user?.uid;
    
    if (!uid) throw new Error("Failed to retrieve User ID from Firebase.");

    return await createUserProfile(uid, username, name, UserRole.MASTER_ADMIN);
  } catch (error: any) {
    if (error.code === 'auth/email-already-in-use') {
      throw new Error(`The account '${email}' already exists.`);
    }
    if (error.code === 'auth/weak-password') {
      throw new Error("Password should be at least 6 characters.");
    }
    throw new Error(error.message || "Failed to register Master Admin.");
  }
};

export const loginUser = async (username: string, password: string): Promise<User> => {
  const email = getEmail(username);
  try {
    const userCredential = await auth.signInWithEmailAndPassword(email, password);
    const uid = userCredential.user?.uid;
    
    if (!uid) throw new Error("User ID not found after login");

    try {
      const userDoc = await getDoc(doc(db, USERS_COLLECTION, uid));
      if (userDoc.exists()) {
        const profile = { id: uid, ...userDoc.data() } as User;
        
        // Block Deactivated Users
        if (profile.isActive === false) {
           await auth.signOut();
           throw new Error("Your account has been deactivated. Please contact the administrator.");
        }
        
        return profile;
      } else {
        throw new Error("Account exists in Auth, but user profile is missing in Database.");
      }
    } catch (dbError: any) {
      if (dbError.message && dbError.message.includes("offline")) {
        throw new Error("Login successful, but cannot fetch profile (Offline). Check internet connection.");
      }
      throw dbError;
    }
  } catch (error: any) {
    const msg = error.message || '';
    if (msg.includes('auth/invalid-credential') || error.code === 'auth/wrong-password') {
      throw new Error(`Invalid credentials. Check username/password.`);
    }
    if (error.code === 'auth/network-request-failed') {
      throw new Error("Network error. Please check your connection.");
    }
    throw new Error(msg || "Login failed due to an unknown error.");
  }
};

export const logoutUser = async () => {
  await auth.signOut();
};

export const getCurrentUserProfile = async (uid: string): Promise<User | null> => {
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, uid));
    if (userDoc.exists()) {
      return { id: uid, ...userDoc.data() } as User;
    }
  } catch (err) {
    console.error("Error getting current user profile:", err);
  }
  return null;
};

export const verifyUserCredentials = async (username: string, password: string) => {
  const currentUser = auth.currentUser;
  if (!currentUser || !currentUser.email) {
    throw new Error("No user is currently logged in.");
  }

  const inputEmail = getEmail(username);
  
  if (inputEmail.toLowerCase() !== currentUser.email.toLowerCase()) {
    throw new Error("Username does not match the currently logged-in user.");
  }

  try {
    const credential = firebase.auth.EmailAuthProvider.credential(currentUser.email, password);
    await currentUser.reauthenticateWithCredential(credential);
    return true;
  } catch (error: any) {
    if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      throw new Error("Incorrect password.");
    }
    throw error;
  }
};

// --- Users Management ---

export const getUsers = async (): Promise<User[]> => {
  try {
    const snapshot = await getDocs(collection(db, USERS_COLLECTION));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as User));
  } catch (error) {
    console.error("Failed to fetch users:", error);
    handleFirestoreError(error, OperationType.LIST, USERS_COLLECTION);
    return [];
  }
};

export const saveUser = async (user: Omit<User, 'id'>, password: string) => {
  const secondaryApp = getSecondaryApp();
  const secondaryAuth = secondaryApp.auth();
  
  try {
    const email = getEmail(user.username);
    const userCredential = await secondaryAuth.createUserWithEmailAndPassword(email, password);
    const uid = userCredential.user?.uid;

    if (!uid) throw new Error("Failed to generate UID");

    try {
      await setDoc(doc(db, USERS_COLLECTION, uid), {
        username: user.username,
        name: user.name,
        role: user.role,
        isActive: true, // Default to true for new users
        visibleSections: user.visibleSections || ['sectionA', 'sectionB', 'sectionC', 'sectionD']
      });
    } catch (dbError) {
      handleFirestoreError(dbError, OperationType.WRITE, `${USERS_COLLECTION}/${uid}`);
      throw dbError;
    }

    await secondaryAuth.signOut();
    await secondaryApp.delete();
  } catch (error: any) {
    console.error("Create User Error:", error);
    if (error.code === 'auth/email-already-in-use') {
      throw new Error("Username/Email already exists.");
    }
    throw new Error(error.message);
  }
};

export const updateUser = async (userId: string, data: Partial<User>) => {
  try {
    await updateDoc(doc(db, USERS_COLLECTION, userId), data);
  } catch (e: any) {
    handleFirestoreError(e, OperationType.UPDATE, `${USERS_COLLECTION}/${userId}`);
    throw new Error("Failed to update user profile: " + e.message);
  }
};

export const updateCurrentUserAuth = async (password: string) => {
  const user = auth.currentUser;
  if (!user) throw new Error("Not logged in");
  if (password) await user.updatePassword(password);
};

export const deleteUser = async (userId: string) => {
  try {
    await deleteDoc(doc(db, USERS_COLLECTION, userId));
  } catch (error) {
    handleFirestoreError(error, OperationType.DELETE, `${USERS_COLLECTION}/${userId}`);
    throw error;
  }
};

// --- Surveys ---

export const getSurveys = async (filters?: { userId?: string, limitCount?: number }): Promise<SurveyData[]> => {
  try {
    let q = query(collection(db, SURVEYS_COLLECTION));

    if (filters?.userId) {
      q = query(q, where('submittedBy', '==', filters.userId));
    }

    if (filters?.limitCount) {
      q = query(q, limit(filters.limitCount));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => d.data() as SurveyData);
  } catch (error) {
    console.error("Failed to fetch surveys:", error);
    handleFirestoreError(error, OperationType.LIST, SURVEYS_COLLECTION);
    return [];
  }
};

// Robust Serial Number Generation
// Uses atomic increment on the metadata document to track the last serial number,
// preventing concurrent race conditions and failed-precondition optimistic concurrency errors.
export const getNextSerialNo = async (): Promise<string> => {
  try {
    const metaRef = doc(db, SETTINGS_COLLECTION, 'metadata');
    
    // Atomically increment lastSerialNo to prevent optimistic concurrency conflicts
    await setDoc(metaRef, { lastSerialNo: increment(1) }, { merge: true });
    
    // Fetch the updated document to retrieve the incremented serial number
    const metaSnap = await getDoc(metaRef);
    const nextVal = metaSnap.exists() ? (metaSnap.data().lastSerialNo || 1) : 1;
    
    return nextVal.toString().padStart(4, '0');
  } catch (e) {
    console.error("Serial generation failed:", e);
    // Emergency fallback to prevent blocking form submissions in case of failure
    return '0000-' + Math.floor(Math.random() * 10000);
  }
};

// Utility to fix existing inconsistent data
export const repairSerialNumbers = async (): Promise<string> => {
  try {
    const snapshot = await getDocs(collection(db, SURVEYS_COLLECTION));
    const surveys = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as SurveyData));
    
    // Sort by submission date (oldest first)
    // We want to keep the ID of the oldest entry in case of duplicates
    surveys.sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime());

    const seenSerials = new Set<string>();
    const seenMemberSerials = new Set<string>();
    const batch = writeBatch(db);
    let updatesCount = 0;
    
    // 1. Calculate the true maximum serial currently in existence
    let maxSerial = 0;
    let maxLS = 0;
    let maxCC = 0;
    let maxFL = 0;
    
    // Fetch dynamic sections and questions to do dynamic repeater updates
    const questions = await getDynamicQuestions();
    const sections = await getDynamicSections();
    const repeaterSecs = sections.filter(s => s.isRepeater);
    const repeaterIds = repeaterSecs.length > 0 ? repeaterSecs.map(s => s.id) : ['sectionB'];

    surveys.forEach(s => {
       const currentStr = s.sectionA?.serialNo;
       const num = parseInt(currentStr || '0', 10);
       if (!isNaN(num) && num > maxSerial) maxSerial = num;

       const pSerial = s.sectionA?.studentSerialNo || s.studentSerialNo || '';
       if (pSerial.startsWith('LS-')) {
           const v = parseInt(pSerial.split('-')[1] || '0', 10);
           if (!isNaN(v) && v > maxLS) maxLS = v;
       } else if (pSerial.startsWith('CC-')) {
           const v = parseInt(pSerial.split('-')[1] || '0', 10);
           if (!isNaN(v) && v > maxCC) maxCC = v;
       } else if (pSerial.startsWith('FL-')) {
           const v = parseInt(pSerial.split('-')[1] || '0', 10);
           if (!isNaN(v) && v > maxFL) maxFL = v;
       }

       for (const rId of repeaterIds) {
           const members = s[rId];
           if (Array.isArray(members)) {
               members.forEach(m => {
                   const mSerial = m.serialNo || m.idNo || '';
                   if (mSerial.startsWith('LS-')) {
                       const v = parseInt(mSerial.split('-')[1] || '0', 10);
                       if (!isNaN(v) && v > maxLS) maxLS = v;
                   } else if (mSerial.startsWith('CC-')) {
                       const v = parseInt(mSerial.split('-')[1] || '0', 10);
                       if (!isNaN(v) && v > maxCC) maxCC = v;
                   } else if (mSerial.startsWith('FL-')) {
                       const v = parseInt(mSerial.split('-')[1] || '0', 10);
                       if (!isNaN(v) && v > maxFL) maxFL = v;
                   }
               });
           }
       }
    });

    console.log("Current detected max household serial:", maxSerial, "LS:", maxLS, "CC:", maxCC, "FL:", maxFL);

    // Get current category counters
    const metaRef = doc(db, SETTINGS_COLLECTION, 'metadata');
    const metaSnap = await getDoc(metaRef);
    const metaData = metaSnap.exists() ? metaSnap.data() : {};
    
    let lastLS = Math.max(metaData.lastLSNo || 0, maxLS);
    let lastCC = Math.max(metaData.lastCCNo || 0, maxCC);
    let lastFL = Math.max(metaData.lastFLNo || 0, maxFL);
    let countersChanged = false;

    // 2. Iterate and fix
    for (const survey of surveys) {
       const currentSerial = survey.sectionA?.serialNo;
       
       const isDuplicate = seenSerials.has(currentSerial);
       const isInvalid = !currentSerial || currentSerial === '0000' || isNaN(parseInt(currentSerial));

       let surveyNeedsUpdate = false;
       let updatedSectionA = { ...(survey.sectionA || {}) };

       if (isDuplicate || isInvalid) {
           // Assign new serial at the end of the chain
           maxSerial++;
           const newSerial = maxSerial.toString().padStart(4, '0');
           console.log(`Fixing survey ${survey.id} household serial. Old: ${currentSerial}, New: ${newSerial}`);
           updatedSectionA.serialNo = newSerial;
           seenSerials.add(newSerial);
           surveyNeedsUpdate = true;
           updatesCount++;
       } else {
           seenSerials.add(currentSerial);
       }

       // Also inspect members and repair any draft/missing serials!
       let updatedSurvey = { ...survey };
       
       const primaryName = getMemberName(updatedSurvey.sectionA || updatedSurvey, questions);
       const primaryAge = getMemberAge(updatedSurvey.sectionA || updatedSurvey, questions);
       const primaryCategory = getCategoryForAge(primaryAge);
       
       if (primaryName && primaryCategory) {
           const mainSource = updatedSurvey.sectionA ? updatedSurvey.sectionA : updatedSurvey;
           const currentPrimarySerial = mainSource.studentSerialNo || '';
           const isMemberDraft = !currentPrimarySerial || currentPrimarySerial.toLowerCase().includes('draft');
           const isMemberDup = currentPrimarySerial && seenMemberSerials.has(currentPrimarySerial);
           
           if (isMemberDraft || isMemberDup) {
              let nextNum = 0;
              if (primaryCategory === 'LS') {
                 lastLS++;
                 nextNum = lastLS;
              } else if (primaryCategory === 'CC') {
                 lastCC++;
                 nextNum = lastCC;
              } else if (primaryCategory === 'FL') {
                 lastFL++;
                 nextNum = lastFL;
              }
              const serialNo = `${primaryCategory}-${nextNum.toString().padStart(4, '0')}`;
              mainSource.studentSerialNo = serialNo;
              mainSource.studentIdNo = serialNo;
              seenMemberSerials.add(serialNo);
              surveyNeedsUpdate = true;
              countersChanged = true;
           } else if (currentPrimarySerial) {
              seenMemberSerials.add(currentPrimarySerial);
           }
       }

       for (const rId of repeaterIds) {
         const members = survey[rId];
         if (Array.isArray(members)) {
           let membersChanged = false;
           const updatedMembers = members.map((member) => {
             const name = getMemberName(member, questions);
             const age = getMemberAge(member, questions);
             const category = getCategoryForAge(age);
             
             if (name && category) {
               const currentMemberSerial = member.serialNo || '';
               const isMemberDraft = !currentMemberSerial || currentMemberSerial.toLowerCase().includes('draft');
               const isMemberDup = currentMemberSerial && seenMemberSerials.has(currentMemberSerial);

               if (isMemberDraft || isMemberDup) {
                 let nextNum = 0;
                 if (category === 'LS') {
                   lastLS++;
                   nextNum = lastLS;
                 } else if (category === 'CC') {
                   lastCC++;
                   nextNum = lastCC;
                 } else if (category === 'FL') {
                   lastFL++;
                   nextNum = lastFL;
                 }
                 
                 const serialNo = `${category}-${nextNum.toString().padStart(4, '0')}`;
                 membersChanged = true;
                 countersChanged = true;
                 console.log(`Fixing member ${name} in survey ${survey.id}. New ID: ${serialNo}`);
                 const newMember = {
                   ...member,
                   serialNo,
                   idNo: serialNo,
                   id_no: serialNo,
                   id: serialNo,
                   categoryName: getCategoryName(category)
                 };
                 seenMemberSerials.add(newMember.serialNo);
                 return newMember;
               }
             }
             if (member.serialNo) seenMemberSerials.add(member.serialNo);
             return member;
           });

           if (membersChanged) {
             updatedSurvey[rId] = updatedMembers;
             surveyNeedsUpdate = true;
             updatesCount++;
           }
         }
       }

       if (surveyNeedsUpdate) {
         const docRef = doc(db, SURVEYS_COLLECTION, survey.id);
         updatedSurvey.sectionA = updatedSectionA;
         batch.update(docRef, updatedSurvey);
       }
    }

    // 3. Update the global metadata counters
    const metadataUpdates: any = { 
        lastSerialNo: maxSerial,
        lastLSNo: lastLS,
        lastCCNo: lastCC,
        lastFLNo: lastFL
    };
    
    if (updatesCount > 0 || countersChanged) {
         batch.set(metaRef, metadataUpdates, { merge: true });
         await batch.commit();
         return `Repaired ${updatesCount} issues. Counters synchronized to max.`;
    } else {
         await setDoc(metaRef, metadataUpdates, { merge: true });
         return `No duplicate serials found. Counters synced to max values (Household: ${maxSerial}, LS: ${lastLS}, CC: ${lastCC}, FL: ${lastFL}).`;
    }

  } catch (error) {
     console.error("Repair error", error);
     throw new Error("Repair failed: " + error.message);
  }
};

export const getCategoryForAge = (age: number): 'LS' | 'CC' | 'FL' | null => {
  if (isNaN(age) || age < 4) return null;
  if (age >= 4 && age <= 8) return 'LS';
  if (age >= 9 && age <= 12) return 'CC';
  if (age >= 13 && age <= 21) return 'FL'; // Student age cap at 21
  return null;
};

export const getCategoryName = (category: string | null): string => {
  if (category === 'LS') return 'Little Stars';
  if (category === 'CC') return 'Creative Champs';
  if (category === 'FL') return 'Future Leaders';
  return '';
};

export const parseDateRobustly = (val: any): Date | null => {
  if (val === undefined || val === null || val === '') return null;
  if (val instanceof Date) {
    return isNaN(val.getTime()) ? null : val;
  }
  const str = String(val).trim();
  if (!str) return null;

  // Handle Excel Serial Numbers (e.g., 43444)
  if (/^\d{5}$/.test(str)) {
    const serial = parseInt(str, 10);
    const excelEpoch = new Date(1899, 11, 30);
    const d = new Date(excelEpoch.getTime() + serial * 24 * 60 * 60 * 1000);
    if (!isNaN(d.getTime())) return d;
  }

  // Match DD/MM/YYYY or D/M/YYYY (or with dashes/dots)
  const separatorMatch = str.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (separatorMatch) {
    const part1 = parseInt(separatorMatch[1], 10);
    const part2 = parseInt(separatorMatch[2], 10);
    let year = parseInt(separatorMatch[3], 10);
    
    if (year < 100) {
      year = year > 50 ? 1900 + year : 2000 + year;
    }

    let day = part1;
    let month = part2;
    
    if (part1 > 12 && part2 <= 12) {
      day = part1;
      month = part2;
    } else if (part2 > 12 && part1 <= 12) {
      day = part2;
      month = part1;
    } else {
      // Default to DD/MM/YYYY for ambiguous cases like 6/12/2018 (Day=6, Month=12)
      day = part1;
      month = part2;
    }

    const d = new Date(year, month - 1, day);
    if (!isNaN(d.getTime())) return d;
  }

  // Match YYYY-MM-DD or YYYY/MM/DD
  const isoMatch = str.match(/^(\d{4})[/\-.](\d{1,2})[/\-.](\d{1,2})$/);
  if (isoMatch) {
    const year = parseInt(isoMatch[1], 10);
    const month = parseInt(isoMatch[2], 10);
    const day = parseInt(isoMatch[3], 10);
    const d = new Date(year, month - 1, day);
    if (!isNaN(d.getTime())) return d;
  }

  // Default fallback
  const d = new Date(str);
  if (!isNaN(d.getTime())) return d;

  return null;
};

export const getMemberAge = (member: any, questions: any[]): number => {
  if (!member || typeof member !== 'object') return NaN;
  
  // 1. Look for fields in questions
  for (const q of questions) {
    const val = member[q.id];
    if (val !== undefined && val !== null && val !== '') {
      const labelLower = String(q.label || '').toLowerCase();
      // Check if this is a DOB field
      if (
        labelLower.includes('dob') || 
        labelLower.includes('birth') || 
        labelLower.includes('ജനന') || 
        labelLower.includes('birthdate') || 
        labelLower.includes('തീയതി') || 
        labelLower.includes('date of birth') ||
        q.type === 'date'
      ) {
        const birthDate = parseDateRobustly(val);
        if (birthDate && !isNaN(birthDate.getTime())) {
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const m = today.getMonth() - birthDate.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          return age;
        }
      }
      // Check if this is an Age field
      if (
        labelLower.includes('age') || 
        labelLower.includes('വയസ്സ്') || 
        labelLower.includes('പ്രായം') || 
        labelLower.includes('വയസ്')
      ) {
        const ageNum = parseInt(String(val), 10);
        if (!isNaN(ageNum)) {
          return ageNum;
        }
      }
    }
  }
  
  // 2. Fallback: check raw object keys and values (e.g. if question keys are dynamic / numbers)
  for (const key of Object.keys(member)) {
    const val = member[key];
    if (val !== undefined && val !== null && val !== '') {
      // If the value matches standard HTML date picker format YYYY-MM-DD
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
        const birthDate = parseDateRobustly(val);
        if (birthDate && !isNaN(birthDate.getTime())) {
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const m = today.getMonth() - birthDate.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          if (age >= 0 && age <= 120) {
            return age;
          }
        }
      }
      
      const keyLower = key.toLowerCase();
      if (
        keyLower.includes('dob') || 
        keyLower.includes('birth') || 
        keyLower.includes('ജനന') || 
        keyLower.includes('birthdate') || 
        keyLower.includes('തീയതി') || 
        keyLower.includes('date of birth')
      ) {
        const birthDate = parseDateRobustly(val);
        if (birthDate && !isNaN(birthDate.getTime())) {
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const m = today.getMonth() - birthDate.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          return age;
        }
      } else if (
        keyLower.includes('age') || 
        keyLower.includes('വയസ്സ്') || 
        keyLower.includes('പ്രായം') || 
        keyLower.includes('വയസ്')
      ) {
        const ageNum = parseInt(String(val), 10);
        if (!isNaN(ageNum)) {
          return ageNum;
        }
      }
    }
  }
  
  return NaN;
};

export const getMemberName = (member: any, questions: any[]): string => {
  if (!member || typeof member !== 'object') return '';
  
  // 0.0 CHECK studentName or student_name explicitly first!
  const explicitKeys = ['studentName', 'student_name', 'childName', 'fullName', 'fullNameMalayalam', 'name', 'memberName'];
  for (const key of explicitKeys) {
    if (member[key] && typeof member[key] === 'string' && member[key].trim().length > 0) {
      return String(member[key]).trim();
    }
  }

  // Helper to identify keys or labels related to hobbies, schools, sports, and other non-person-name fields
  const isNonNameField = (str: string): boolean => {
    const s = str.toLowerCase();
    return s.includes('hobby') || s.includes('hobbies') || s.includes('school') || s.includes('സ്ഥാപനം') || s.includes('വിനോദം') || s.includes('കലകൾ') || s.includes('കായികം') || s.includes('ക്ലാസ്') || s.includes('class') || s.includes('standard') || s.includes('തൊഴിൽ') || 
           s.includes('parent') || s.includes('father') || s.includes('mother') || s.includes('guardian') || s.includes('email') ||
           s.includes('രക്ഷിതാവ്') || s.includes('രക്ഷിതാ') || s.includes('മാതാവ്') || s.includes('മാതാ') || s.includes('അമ്മ') || s.includes('ഉമ്മ') ||
           s.includes('പിതാവ്') || s.includes('പിതാ') || s.includes('അച്ഛൻ') || s.includes('ഉപ്പ') || s.includes('fathername') || s.includes('mothername') ||
           s.includes('football') || s.includes('cricket') || s.includes('drawing') || s.includes('painting') || s.includes('singing') || s.includes('music') || s.includes('reading') || s.includes('traveling') || s.includes('cooking') || s.includes('badminton') || s.includes('sports') || s.includes('gaming') || s.includes('dance') || s.includes('dancing') || s.includes('art') || s.includes('craft') ||
           s.includes('ഫുട്ബോൾ') || s.includes('ക്രിക്കറ്റ്') || s.includes('വരക്കൽ') || s.includes('പാട്ട്') || s.includes('പാട്ടുപാടൽ') || s.includes('വായന') || s.includes('പാചകം') || s.includes('കളി');
  };

  // 0. User explicitly requested: Use the 1st question in Section A that isn't the Mother or ID No.
  if (questions && Array.isArray(questions)) {
    const sectionAQs = questions.filter(q => q.section === 'sectionA' || !q.section);
    for (const q of sectionAQs) {
      if (q.type !== 'text') continue;
      const val = member[q.id];
      if (val && typeof val === 'string' && val.trim().length > 0) {
        const labelLower = String(q.label || '').toLowerCase();
        const idLower = String(q.id || '').toLowerCase();
        
        // Skip explicitly non-name fields
        if (isNonNameField(labelLower) || isNonNameField(idLower)) continue;
        
        const idNormalized = idLower.replace(/[.\s_-]/g, '');
        const labelNormalized = labelLower.replace(/[.\s_-]/g, '');

        // Skip ID and Address fields
        const isIdField = idNormalized === 'id' || 
                          idNormalized.includes('idno') || 
                          idNormalized.includes('serial') || 
                          idNormalized.includes('number') ||
                          idNormalized.includes('no') ||
                          idNormalized.includes('reg') ||
                          idNormalized.includes('admission') ||
                          idNormalized.includes('roll') ||
                          labelNormalized.includes('idno') || 
                          labelNormalized.includes('serial') || 
                          labelNormalized.includes('നമ്പർ') || 
                          labelNormalized.includes('number') ||
                          labelNormalized.includes('idno') ||
                          labelNormalized.includes('admission') ||
                          labelNormalized.includes('reg') ||
                          labelNormalized.includes('roll');

        const isAddressField = idNormalized.includes('house') || labelNormalized.includes('house') || labelNormalized.includes('വീട്') ||
                               idNormalized.includes('ward') || labelNormalized.includes('ward') || labelNormalized.includes('വാർഡ്') ||
                               idNormalized.includes('phone') || labelNormalized.includes('phone') || labelNormalized.includes('മൊബൈൽ') ||
                               idNormalized.includes('mobile') || labelNormalized.includes('mobile');

        if (isIdField || isAddressField) continue;

        // If it passes all the above, this is the first valid text question in Section A!
        return String(val).trim();
      }
    }
  }

  // 1. Try to find an EXPLICIT student name field in questions first (Highest Priority)
  if (questions && Array.isArray(questions)) {
    for (const q of questions) {
      const val = member[q.id];
      if (val !== undefined && val !== null && val !== '') {
        const labelLower = String(q.label || '').toLowerCase();
        const idLower = String(q.id || '').toLowerCase();
        
        if (isNonNameField(labelLower) || isNonNameField(idLower)) continue;

        if (
          (labelLower.includes('student') && labelLower.includes('name')) ||
          (labelLower.includes('child') && labelLower.includes('name')) ||
          labelLower.includes('വിദ്യാർത്ഥിയുടെ പേര്') ||
          labelLower.includes('കുട്ടിയുടെ പേര്') ||
          idLower.includes('studentname') ||
          idLower.includes('student_name') ||
          idLower.includes('student') ||
          idLower.includes('child')
        ) {
          return String(val).trim();
        }
      }
    }
  }

  // 2. Direct key priority (especially for student name over parent/hobbies)
  const priorityKeys = ['studentName', 'student_name', 'childName', 'fullName', 'fullNameMalayalam', 'name', 'memberName'];
  for (const key of priorityKeys) {
    if (member[key] && typeof member[key] === 'string' && member[key].trim().length > 0) {
      return String(member[key]).trim();
    }
  }

  // 3. Try to find a field in questions (PASS B: Any other person-name matching fields)
  if (questions && Array.isArray(questions)) {
    for (const q of questions) {
      const val = member[q.id];
      if (val !== undefined && val !== null && val !== '') {
        const labelLower = String(q.label || '').toLowerCase();
        const idLower = String(q.id || '').toLowerCase();

        if (isNonNameField(labelLower) || isNonNameField(idLower)) continue;

        if (
          labelLower.includes('name') || 
          labelLower.includes('പേര്') || 
          idLower.includes('name') ||
          labelLower.includes('member')
        ) {
          return String(val).trim();
        }
      }
    }
  }
  
  // 4. Fuzzy key search in the object itself
  const nameIndicators = ['name', 'പേര്', 'fullname', 'student', 'child'];
  for (const key of Object.keys(member)) {
    const keyLower = key.toLowerCase();
    if (isNonNameField(keyLower)) continue;
    
    if (nameIndicators.some(ind => keyLower.includes(ind))) {
      const val = member[key];
      if (val && typeof val === 'string' && val.trim().length > 0 && !val.startsWith('data:image')) {
        return String(val).trim();
      }
    }
  }

  // 4. Ultimate fallback: find the first non-ID, non-numeric, non-image string
  for (const key of Object.keys(member)) {
    const val = member[key];
    if (typeof val === 'string' && val.trim().length > 2) {
      const v = val.trim();
      if (
        !v.startsWith('data:image') && 
        !/^\d+$/.test(v) && 
        !key.toLowerCase().includes('id') &&
        !key.toLowerCase().includes('serial') &&
        !key.toLowerCase().includes('phone') &&
        !key.toLowerCase().includes('image') &&
        !key.toLowerCase().includes('photo') &&
        !key.toLowerCase().includes('email') &&
        !v.includes('@') &&
        !v.includes('.com') &&
        !v.includes('http') &&
        !isNonNameField(key) &&
        !isNonNameField(v) &&
        v.length < 100 // Exclude very long strings that might be blobs
      ) {
        return v;
      }
    }
  }
  
  return '';
};

export const getMemberPhone = (member: any, questions: any[]): string => {
  if (!member || typeof member !== 'object') return '';
  
  // 1. Direct keys priority
  const priorityKeys = ['phone', 'mobile', 'whatsapp', 'contact', 'tel', 'parentPhone', 'phoneNumber', 'mobileNumber'];
  for (const key of priorityKeys) {
    if (member[key]) return String(member[key]).trim();
  }

  // 2. Question-based check
  if (questions && Array.isArray(questions)) {
    for (const q of questions) {
      const val = member[q.id];
      if (val) {
        const labelLower = String(q.label || '').toLowerCase();
        const idLower = String(q.id).toLowerCase();
        if (
          labelLower.includes('phone') || 
          labelLower.includes('mobile') || 
          labelLower.includes('ഫോൺ') || 
          labelLower.includes('മൊബൈൽ') || 
          labelLower.includes('നമ്പർ') ||
          idLower.includes('phone') ||
          idLower.includes('mobile')
        ) {
          return String(val).trim();
        }
      }
    }
  }
  
  // 3. Fuzzy search
  for (const key of Object.keys(member)) {
    const keyLower = key.toLowerCase();
    if (keyLower.includes('phone') || keyLower.includes('mobile') || keyLower.includes('contact')) {
      return String(member[key]).trim();
    }
  }
  return '';
};

export const getMemberIICStatus = (member: any, questions: any[]): boolean => {
  if (!member || typeof member !== 'object') return false;
  
  // 1. Direct key check
  const priorityKeys = ['isIICMember', 'iicMember', 'membership', 'isMember', 'iic_member', 'memberStatus'];
  for (const key of priorityKeys) {
    const val = member[key];
    if (val === true || val === 'true' || val === 'Yes' || (typeof val === 'string' && val.toLowerCase() === 'true')) return true;
  }

  // 2. Question-based check
  if (questions && Array.isArray(questions)) {
    for (const q of questions) {
      const val = member[q.id];
      if (val !== undefined && val !== null && val !== '') {
        const labelLower = String(q.label || '').toLowerCase();
        const idLower = String(q.id).toLowerCase();
        if (
          labelLower.includes('iic') || 
          labelLower.includes('ഐ.ഐ.സി') || 
          labelLower.includes('centre member') || 
          labelLower.includes('സെന്റർ മെമ്പർ') ||
          labelLower.includes('മെമ്പർഷിപ്പ്') ||
          labelLower.includes('member') ||
          idLower.includes('iic') ||
          idLower.includes('member')
        ) {
          if (val === true || val === 'true' || val === 'Yes' || (typeof val === 'string' && val.toLowerCase() === 'true')) return true;
          if (typeof val === 'string' && (val.toLowerCase().includes('yes') || val.includes('അതെ'))) return true;
        }
      }
    }
  }

  // 3. Fuzzy key search
  for (const key of Object.keys(member)) {
    const keyLower = key.toLowerCase();
    if (keyLower.includes('member') || keyLower.includes('iic')) {
      const val = member[key];
      if (val === true || val === 'true' || val === 'Yes' || (typeof val === 'string' && val.toLowerCase() === 'true')) return true;
    }
  }

  return false;
};

export const checkExplicitNonMember = (obj: any, questions?: any[]): boolean => {
  if (!obj || typeof obj !== 'object') return false;
  const priorityKeys = ['isIICMember', 'iicMember', 'membership', 'isMember', 'iic_member', 'memberStatus', '1782312069160'];
  for (const key of priorityKeys) {
    const val = obj[key];
    if (val === false || val === 'false' || val === 'No' || (typeof val === 'string' && val.toLowerCase() === 'no')) {
      return true;
    }
  }
  if (questions && Array.isArray(questions)) {
    for (const q of questions) {
      const val = obj[q.id];
      if (val === false || val === 'false' || val === 'No' || (typeof val === 'string' && val.toLowerCase() === 'no')) {
        const labelLower = String(q.label || '').toLowerCase();
        const idLower = String(q.id).toLowerCase();
        if (
          labelLower.includes('iic') || 
          labelLower.includes('member') || 
          idLower.includes('iic') || 
          idLower.includes('member')
        ) {
          return true;
        }
      }
    }
  }
  return false;
};

export const getSurveyIICId = (formData: Record<string, any>, questions: any[]): string | null => {
  const isExplicitlyNonMember = checkExplicitNonMember(formData, questions) || (formData.sectionA && checkExplicitNonMember(formData.sectionA, questions));
  if (isExplicitlyNonMember) {
    return null;
  }

  const searchInObject = (obj: any): string | null => {
    if (!obj || typeof obj !== 'object') return null;
    
    // Look for common membership ID keys
    const commonKeys = [
      'membershipId', 'memberId', 'iicId', 'iicNumber', 'membershipNumber', 
      'memberNumber', 'cardId', 'idNumber', 'iic_id', 'membership_id',
      'iicMemberId', 'iic_member_id'
    ];

    for (const key of Object.keys(obj)) {
      const keyLower = key.toLowerCase();
      if (commonKeys.some(ck => keyLower === ck.toLowerCase())) {
        return obj[key];
      }
    }

    // Fuzzy search
    for (const key of Object.keys(obj)) {
      const keyLower = key.toLowerCase();
      if ((keyLower.includes('iic') || keyLower.includes('member')) && keyLower.includes('id')) {
        return obj[key];
      }
      if ((keyLower.includes('iic') || keyLower.includes('member')) && keyLower.includes('no')) {
        return obj[key];
      }
    }

    // Question-based search
    if (questions && Array.isArray(questions)) {
      for (const q of questions) {
        if (obj[q.id] !== undefined && obj[q.id] !== null && obj[q.id] !== '') {
          const labelLower = String(q.label || '').toLowerCase();
          if ((labelLower.includes('iic') || labelLower.includes('member')) && (labelLower.includes('id') || labelLower.includes('no') || labelLower.includes('number'))) {
            return obj[q.id];
          }
        }
      }
    }

    return null;
  };

  // 1. Check Section A (Household Head)
  if (formData.sectionA) {
    const id = searchInObject(formData.sectionA);
    if (id) return String(id);
  }

  // 2. Check all sections
  for (const sectionId of Object.keys(formData)) {
    const sectionData = formData[sectionId];
    if (Array.isArray(sectionData)) {
      for (const m of sectionData) {
        const id = searchInObject(m);
        if (id) return String(id);
      }
    } else {
      const id = searchInObject(sectionData);
      if (id) return String(id);
    }
  }

  return null;
};

export const getSurveyIICStatus = (formData: Record<string, any>, questions: any[], sections: any[]): boolean => {
  // Check if they are explicitly marked as a non-member.
  // If any of the priority keys or membership-related questions say "No" / false,
  // we do NOT consider them a member (even if there is a membership ID entered).
  const isExplicitlyNonMember = checkExplicitNonMember(formData, questions) || (formData.sectionA && checkExplicitNonMember(formData.sectionA, questions));

  if (isExplicitlyNonMember) {
    return false;
  }

  // If we have a membership ID, they are definitely a member!
  const hasId = getSurveyIICId(formData, questions);
  if (hasId && hasId.trim() !== '') return true;

  // 1. Check Section A (Head of Household)
  if (formData.sectionA && getMemberIICStatus(formData.sectionA, questions)) return true;
  
  // 2. Check all repeater sections
  const repeaterSections = sections.filter(s => s.isRepeater);
  for (const s of repeaterSections) {
    const members = formData[s.id] || [];
    if (members.some((m: any) => getMemberIICStatus(m, questions))) return true;
  }
  
  // 3. Fallback check all top-level sections
  for (const sectionId of Object.keys(formData)) {
    const sectionData = formData[sectionId];
    if (Array.isArray(sectionData)) {
      if (sectionData.some((m: any) => getMemberIICStatus(m, questions))) return true;
    } else if (typeof sectionData === 'object' && sectionData !== null) {
      if (getMemberIICStatus(sectionData, questions)) return true;
    }
  }
  
  return false;
};

export const getHouseholdName = (survey: any, questions: any[]): string => {
  if (!survey) return '';
  
  // 0. High priority explicit keys check
  const sectionAObj = survey.sectionA || {};
  const explicitParentKeys = ['parentName', 'parent_name', 'fatherName', 'father_name', 'guardianName', 'guardian_name'];
  for (const key of explicitParentKeys) {
    if (sectionAObj[key] && typeof sectionAObj[key] === 'string' && sectionAObj[key].trim().length > 0) {
      return String(sectionAObj[key]).trim();
    }
    if (survey[key] && typeof survey[key] === 'string' && survey[key].trim().length > 0) {
      return String(survey[key]).trim();
    }
  }

  // 1. Try to specifically find parent/guardian name based on questions
  if (questions && Array.isArray(questions)) {
    const parentQuestions = questions.filter(q => {
      const labelLower = String(q.label || '').toLowerCase();
      const idLower = String(q.id || '').toLowerCase();
      return labelLower.includes('parent') || labelLower.includes('guardian') || labelLower.includes('father') || labelLower.includes('രക്ഷിതാവ്') || labelLower.includes('രക്ഷിതാ') || idLower.includes('parent');
    });

    for (const q of parentQuestions) {
      // Check section A explicitly
      if (survey.sectionA && survey.sectionA[q.id]) return String(survey.sectionA[q.id]).trim();
      // Check root
      if (survey[q.id]) return String(survey[q.id]).trim();
      
      // Search recursively
      for (const key of Object.keys(survey)) {
        if (key === 'id' || key === 'submittedBy' || key === 'submittedAt') continue;
        const section = survey[key];
        if (section && typeof section === 'object' && !Array.isArray(section) && section[q.id]) {
          return String(section[q.id]).trim();
        }
      }
    }
  }

  // 2. Fallback to common keys explicitly for parent
  const sectionA = survey.sectionA || {};
  if (sectionA.parentName) return String(sectionA.parentName).trim();
  if (sectionA.fatherName) return String(sectionA.fatherName).trim();
  if (sectionA.guardianName) return String(sectionA.guardianName).trim();
  
  if (survey.parentName) return String(survey.parentName).trim();
  if (survey.fatherName) return String(survey.fatherName).trim();
  if (survey.guardianName) return String(survey.guardianName).trim();

  // 3. Fallback to head name
  if (sectionA.headName) return String(sectionA.headName).trim();
  if (survey.headName) return String(survey.headName).trim();

  // 4. Scan all sections that are objects (robust search - try to extract ANY name)
  for (const key of Object.keys(survey)) {
    if (key === 'id' || key === 'submittedBy' || key === 'submittedAt') continue;
    const section = survey[key];
    if (section && typeof section === 'object' && !Array.isArray(section)) {
      if (section.parentName) return String(section.parentName).trim();
    }
  }
  
  return '';
};


export const getHouseholdLocation = (survey: any, questions: any[]): string => {
  if (!survey) return '';
  
  // 1. Direct keys
  const priorityKeys = ['location', 'zone', 'area', 'address', 'city', 'region', '1782312786621'];
  const checkObj = (obj) => {
    if (!obj || typeof obj !== 'object') return '';
    for (const key of priorityKeys) {
      if (obj[key] && typeof obj[key] === 'string' && obj[key].trim().length > 0) {
        return String(obj[key]).trim();
      }
    }
    
    // Check questions
    if (questions && Array.isArray(questions)) {
      for (const q of questions) {
        const lowerLabel = (q.label || '').toLowerCase();
        if (lowerLabel.includes('location') || lowerLabel.includes('zone') || lowerLabel.includes('area') || lowerLabel.includes('address') || lowerLabel.includes('സ്ഥലം') || lowerLabel.includes('ഏരിയ') || lowerLabel.includes('സ്ഥലം/സോൺ')) {
           if (obj[q.id] && typeof obj[q.id] === 'string' && obj[q.id].trim().length > 0) {
              return String(obj[q.id]).trim();
           }
        }
      }
    }
    
    // scan keys
    for (const key of Object.keys(obj)) {
      const lowerKey = key.toLowerCase();
      if (lowerKey.includes('location') || lowerKey.includes('zone') || lowerKey.includes('area') || lowerKey.includes('address')) {
         if (typeof obj[key] === 'string' && obj[key].trim().length > 0) {
            return String(obj[key]).trim();
         }
      }
    }
    return '';
  };
  
  const fromSectionA = checkObj(survey.sectionA);
  if (fromSectionA) return fromSectionA;
  
  const fromRoot = checkObj(survey);
  if (fromRoot) return fromRoot;
  
  // Scan sections
  for (const key of Object.keys(survey)) {
    if (key === 'id' || key === 'submittedBy' || key === 'submittedAt') continue;
    const section = survey[key];
    if (section && typeof section === 'object' && !Array.isArray(section)) {
      const loc = checkObj(section);
      if (loc) return loc;
    }
  }
  
  return '';
};

export const getHouseholdPhone = (survey: any, questions: any[]): string => {
  if (!survey) return '';
  
  // 1. Check sectionA explicitly
  const sectionA = survey.sectionA || {};
  if (sectionA.phone) return String(sectionA.phone).trim();
  
  // 2. Check root
  if (survey.phone) return String(survey.phone).trim();

  // 3. Scan all sections that are objects
  for (const key of Object.keys(survey)) {
    if (key === 'id' || key === 'submittedBy' || key === 'submittedAt') continue;
    const section = survey[key];
    if (section && typeof section === 'object' && !Array.isArray(section)) {
      const phone = getMemberPhone(section, questions);
      if (phone) return phone;
    }
  }

  // 4. Fallback to root scan
  return getMemberPhone(survey, questions) || '';
};

export const getHouseholdSerialNo = (survey: any): string => {
  if (!survey) return '';
  if (survey.sectionA && survey.sectionA.serialNo) return survey.sectionA.serialNo;
  if (survey.serialNo) return survey.serialNo;
  for (const key of Object.keys(survey)) {
    const val = survey[key];
    if (val && typeof val === 'object' && !Array.isArray(val) && val.serialNo) return val.serialNo;
  }
  return '';
};

export const convertGoogleDriveUrl = (url: string): string => {
  if (!url || typeof url !== 'string') return url;
  
  let trimmed = url.trim();
  // Ensure protocol is attached if missing
  if (trimmed.includes('drive.google.com') && !trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    trimmed = 'https://' + trimmed;
  }
  
  // Check if it is a Google Drive link
  if (trimmed.includes('drive.google.com') || trimmed.includes('docs.google.com')) {
    let fileId = '';
    
    // Match /file/d/FILE_ID
    const fileDMatch = trimmed.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (fileDMatch && fileDMatch[1]) {
      fileId = fileDMatch[1];
    } else {
      // Match ?id=FILE_ID or &id=FILE_ID
      const idMatch = trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (idMatch && idMatch[1]) {
        fileId = idMatch[1];
      }
    }
    
    if (fileId) {
      // Return a direct embeddable image link using googleusercontent wrapped with Google's public OpenSocial CORS proxy.
      // This allows the image to be loaded within canvasses for printing/editing without causing CORS tainted canvas issues,
      // and guarantees that cookie blocks do not prevent loading.
      const directUrl = `https://drive.google.com/thumbnail?id=${fileId}&sz=w800`;
      return `https://images1-focus-opensocial.googleusercontent.com/gadgets/proxy?container=focus&refresh=2592000&url=${encodeURIComponent(directUrl)}`;
    }
  }
  
  return trimmed;
};

export const getMemberPhoto = (member: any, questions: any[]): string => {
  if (!member || typeof member !== 'object') return '';
  
  // 1. Look for fields of type 'image' in questions
  const imageQuestions = questions.filter(q => q.type === 'image');
  for (const q of imageQuestions) {
    const val = member[q.id];
    if (val && typeof val === 'string') {
      const trimmed = val.trim();
      if (trimmed.startsWith('data:image') || trimmed.startsWith('http') || trimmed.includes('drive.google.com') || trimmed.includes('docs.google.com')) {
        return convertGoogleDriveUrl(trimmed);
      }
    }
  }

  // 2. Fallback to common keys
  const standardKeys = ['housePhoto', 'photo', 'image', 'studentPhoto', 'memberPhoto', 'profilePhoto', 'house_photo'];
  for (const key of standardKeys) {
    const val = member[key];
    if (val && typeof val === 'string') {
      const trimmed = val.trim();
      if (trimmed.startsWith('data:image') || trimmed.startsWith('http') || trimmed.includes('drive.google.com') || trimmed.includes('docs.google.com')) {
        return convertGoogleDriveUrl(trimmed);
      }
    }
  }

  // 3. Fallback to any string starting with data:image or containing google drive domains
  for (const key of Object.keys(member)) {
    const val = member[key];
    if (val && typeof val === 'string') {
      const trimmed = val.trim();
      if (trimmed.startsWith('data:image')) {
        return trimmed;
      }
      if (trimmed.includes('drive.google.com') || trimmed.includes('docs.google.com')) {
        return convertGoogleDriveUrl(trimmed);
      }
    }
  }

  return '';
};

export const getHouseholdPhoto = (survey: any, questions: any[]): string => {
  if (!survey) return '';
  
  // 1. Check root
  const rootPhoto = getMemberPhoto(survey, questions);
  if (rootPhoto) return rootPhoto;

  // 2. Scan all sections
  for (const key of Object.keys(survey)) {
    if (key === 'id' || key === 'submittedBy' || key === 'submittedAt') continue;
    const section = survey[key];
    if (section && typeof section === 'object' && !Array.isArray(section)) {
      const photo = getMemberPhoto(section, questions);
      if (photo) return photo;
    }
  }

  return '';
};

const cleanUndefined = (obj: any): any => {
  if (obj === undefined || obj === null) return obj;
  
  if (typeof obj === 'function' || typeof obj === 'symbol') {
    return undefined;
  }

  if (obj instanceof Date) {
    if (isNaN(obj.getTime())) return null;
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj
      .map(v => {
        if (Array.isArray(v)) return JSON.stringify(v); // Prevent nested arrays
        return cleanUndefined(v);
      })
      .filter(v => v !== undefined);
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  // Handle custom objects like File, Blob which cause Firestore errors
  const isPlain = Object.prototype.toString.call(obj) === '[object Object]';
  if (!isPlain) {
    // If it's something weird, stringify it or return null
    try {
      return String(obj);
    } catch (e) {
      return null;
    }
  }

  const cleanObj: any = {};
  for (const key of Object.keys(obj)) {
    const val = obj[key];
    const cleaned = cleanUndefined(val);
    if (cleaned !== undefined) {
      if (key === "array") { cleanObj["_array"] = cleaned; } else { cleanObj[key] = cleaned; }
    }
  }
  return cleanObj;
};

export const saveSurvey = async (survey: SurveyData) => {
  let finalSurvey = cleanUndefined({ ...survey });
  
  try {
    const questions = await getDynamicQuestions();
    const sections = await getDynamicSections();
    const repeaterSecs = sections.filter(s => s.isRepeater);
    const repeaterIds = repeaterSecs.length > 0 ? repeaterSecs.map(s => s.id) : ['sectionB'];

    await runTransaction(db, async (transaction) => {
      const metaRef = doc(db, SETTINGS_COLLECTION, 'metadata');
      const metaSnap = await transaction.get(metaRef);
      const metaData = metaSnap.exists() ? metaSnap.data() : {};
      
      let { lastLSNo: lastLS = 0, lastCCNo: lastCC = 0, lastFLNo: lastFL = 0, lastSerialNo: lastGlobal = 0 } = metaData;
      let updatedMetadata: Record<string, number> = {};
      let metadataChanged = false;
      
      // Also process the primary student in sectionA or flat data!
      const primaryName = getMemberName(finalSurvey.sectionA || finalSurvey, questions);
      const primaryAge = getMemberAge(finalSurvey.sectionA || finalSurvey, questions);
      const primaryCategory = getCategoryForAge(primaryAge);
      if (primaryName && primaryCategory) {
          const mainSource = finalSurvey.sectionA ? finalSurvey.sectionA : finalSurvey;
          // IMPORTANT: we only assign a student ID to 'studentSerialNo' or similar, 
          // because 'serialNo' at the root/sectionA is the Household Registration ID!
          const currentPrimarySerial = mainSource.studentSerialNo || '';
          if (!currentPrimarySerial || currentPrimarySerial.toLowerCase().includes('draft')) {
              let nextNum = 0;
              if (primaryCategory === 'LS') { nextNum = ++lastLS; updatedMetadata.lastLSNo = lastLS; }
              else if (primaryCategory === 'CC') { nextNum = ++lastCC; updatedMetadata.lastCCNo = lastCC; }
              else if (primaryCategory === 'FL') { nextNum = ++lastFL; updatedMetadata.lastFLNo = lastFL; }
              
              if (nextNum > 0) {
                  metadataChanged = true;
                  const newSerial = `${primaryCategory}-${nextNum.toString().padStart(4, '0')}`;
                  mainSource.studentSerialNo = newSerial;
                  mainSource.studentIdNo = newSerial;
              }
          }
      }

      for (const rId of repeaterIds) {
        const members = finalSurvey[rId];
        if (Array.isArray(members)) {
          finalSurvey[rId] = members.map((member: any) => {
            const name = getMemberName(member, questions);
            const age = getMemberAge(member, questions);
            const category = getCategoryForAge(age);
            
            if (name && category) {
              const currentSerial = member.serialNo || member.idNo || '';
              if (!currentSerial || currentSerial.toLowerCase().includes('draft')) {
                let nextNum = 0;
                if (category === 'LS') { nextNum = ++lastLS; updatedMetadata.lastLSNo = lastLS; }
                else if (category === 'CC') { nextNum = ++lastCC; updatedMetadata.lastCCNo = lastCC; }
                else if (category === 'FL') { nextNum = ++lastFL; updatedMetadata.lastFLNo = lastFL; }
                
                if (nextNum > 0) {
                  metadataChanged = true;
                  const newSerial = `${category}-${nextNum.toString().padStart(4, '0')}`;
                  return { 
                    ...member, 
                    serialNo: newSerial, 
                    idNo: newSerial,
                    id_no: newSerial,
                    id: newSerial,
                    categoryName: getCategoryName(category)
                  };
                }
              }
            }
            return member;
          });
        }
      }

      if (finalSurvey.sectionA?.serialNo) {
         const currentVal = parseInt(finalSurvey.sectionA.serialNo, 10);
         if (!isNaN(currentVal) && currentVal > lastGlobal) {
            updatedMetadata.lastSerialNo = currentVal;
            metadataChanged = true;
         }
      }

      const surveyRef = doc(db, SURVEYS_COLLECTION, finalSurvey.id);
      transaction.set(surveyRef, finalSurvey);
      
      if (metadataChanged) {
        transaction.set(metaRef, updatedMetadata, { merge: true });
      }
    });

    return finalSurvey;
  } catch (error) {
    console.error("Critical Save Error:\n", error, "\nFINAL_SURVEY:\n", JSON.stringify(finalSurvey));
      if (error.message && error.message.includes("nested entity")) {
         try {
           
           await setDoc(doc(db, 'surveys', 'debug_payload_last_error'), { 
             id: 'debug_payload_last_error',
             submittedBy: 'system',
             submittedAt: new Date().toISOString(),
             payload: JSON.stringify(finalSurvey), 
             errorMessage: error.message 
           });
         } catch(e) { console.error("Failed to save debug doc", e); }
      }
    // Fallback save attempt
    await setDoc(doc(db, SURVEYS_COLLECTION, finalSurvey.id), finalSurvey);
    return finalSurvey;
  }
};

export const deleteSurvey = async (id: string) => {
  try {
    await deleteDoc(doc(db, SURVEYS_COLLECTION, id));
  } catch (error) {
    handleFirestoreError(error, OperationType.DELETE, `${SURVEYS_COLLECTION}/${id}`);
    throw error;
  }
};

export const deleteSurveys = async (ids: string[]) => {
  try {
    const batch = writeBatch(db);
    ids.forEach((id) => {
      batch.delete(doc(db, SURVEYS_COLLECTION, id));
    });
    await batch.commit();
  } catch (error) {
    handleFirestoreError(error, OperationType.DELETE, `${SURVEYS_COLLECTION} (batch size: ${ids?.length})`);
    throw error;
  }
};

export const resetSurveys = async () => {
  try {
    const snapshot = await getDocs(collection(db, SURVEYS_COLLECTION));
    const batch = writeBatch(db);
    snapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });
    // Also reset metadata
    batch.set(doc(db, SETTINGS_COLLECTION, 'metadata'), { lastSerialNo: 0 });
    await batch.commit();
  } catch (error) {
    handleFirestoreError(error, OperationType.DELETE, SURVEYS_COLLECTION);
    throw error;
  }
};

export const clearImportedSurveys = async () => {
  try {
    const snapshot = await getDocs(collection(db, SURVEYS_COLLECTION));
    const batch = writeBatch(db);
    let count = 0;
    snapshot.docs.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.isImported === true) {
        batch.delete(docSnap.ref);
        count++;
      }
    });
    if (count > 0) {
      await batch.commit();
    }
    return count;
  } catch (error) {
    handleFirestoreError(error, OperationType.DELETE, SURVEYS_COLLECTION + " (imported only)");
    throw error;
  }
};

// --- Settings (Questions & Sections) ---

export const getDynamicSections = async (): Promise<SurveySection[]> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'sections'));
    if (docSnap.exists()) {
      return (docSnap.data().list as SurveySection[]).sort((a,b) => a.order - b.order);
    }
  } catch (error) {
    console.warn("Error fetching sections:", error);
  }
  return [];
}

export const saveSections = async (sections: SurveySection[]) => {
  await setDoc(doc(db, SETTINGS_COLLECTION, 'sections'), { list: sections });
};

export const getDynamicQuestions = async (section?: string): Promise<DynamicQuestion[]> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'questions'));
    if (docSnap.exists()) {
      const all = docSnap.data().list as DynamicQuestion[];
      if (section) return all.filter(q => q.section === section);
      return all;
    }
  } catch (error) {
    // console.warn("Error fetching questions (likely permissions):", error);
  }
  return [];
};

export const saveDynamicQuestion = async (q: DynamicQuestion) => {
  const current = await getDynamicQuestions();
  const index = current.findIndex(item => item.id === q.id);
  
  let newList = [...current];
  if (index >= 0) {
    newList[index] = q;
  } else {
    newList.push(q);
  }
  
  await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: newList });
};

export const saveQuestionsOrder = async (questions: DynamicQuestion[]) => {
  await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: questions });
};

export const deleteDynamicQuestion = async (id: string) => {
  const current = await getDynamicQuestions();
  const newList = current.filter(q => q.id !== id);
  await setDoc(doc(db, SETTINGS_COLLECTION, 'questions'), { list: newList });
};

export const saveTemplate = async (template: CardTemplate) => {
  const dataToSave = { ...template };
  if (dataToSave.photoConfig === undefined) {
    dataToSave.photoConfig = null;
  }
  await setDoc(doc(db, SETTINGS_COLLECTION, 'template'), dataToSave);
};

export const getTemplate = async (): Promise<CardTemplate | null> => {
  try {
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'template'));
    return docSnap.exists() ? docSnap.data() as CardTemplate : null;
  } catch (e) {
    console.warn("Failed to get template:", e);
    return null;
  }
};
export const getBusAssignments = async (driverId: string) => {
  const docRef = doc(db, 'bus_assignments', driverId);
  const snap = await getDoc(docRef);
  return snap.exists() ? snap.data().assignedStudents || [] : [];
};

export const saveBusAssignments = async (driverId: string, assignedStudents: any[]) => {
  const docRef = doc(db, 'bus_assignments', driverId);
  await setDoc(docRef, { assignedStudents }, { merge: true });
};

export const subscribeBusJourneys = (callback: (journeys: BusJourney[]) => void) => {
  const q = query(collection(db, 'bus_journeys'), orderBy('date', 'desc'));
  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as BusJourney)));
  });
};

export const subscribeDriverActiveJourney = (driverId: string, callback: (journey: BusJourney | null) => void) => {
  const q = query(collection(db, 'bus_journeys'), where('driverId', '==', driverId));
  return onSnapshot(q, (snapshot) => {
    const activeDocs = snapshot.docs.filter(d => ['NOT_STARTED', 'IN_PROGRESS'].includes(d.data().status));
    if (activeDocs.length === 0) {
      callback(null);
    } else {
      const doc = activeDocs[0];
      callback({ id: doc.id, ...doc.data() } as BusJourney);
    }
  });
};

export const createBusJourney = async (journey: Omit<BusJourney, 'id'>) => {
  const newRef = doc(collection(db, 'bus_journeys'));
  await setDoc(newRef, { ...journey, createdAt: new Date().toISOString() });
  return newRef.id;
};

export const updateBusJourney = async (journeyId: string, data: Partial<BusJourney>) => {
  const docRef = doc(db, 'bus_journeys', journeyId);
  await updateDoc(docRef, data);
};

const ATTENDANCE_COLLECTION = 'attendance';

export const getAttendanceByDate = async (date: string): Promise<AttendanceRecord[]> => {
  try {
    const q = query(collection(db, ATTENDANCE_COLLECTION), where('date', '==', date));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AttendanceRecord));
  } catch (e) {
    console.error("Error getting attendance", e);
    handleFirestoreError(e, OperationType.LIST, ATTENDANCE_COLLECTION);
    return [];
  }
};

export const subscribeAttendanceByDate = (date: string, callback: (records: AttendanceRecord[]) => void) => {
  const q = query(collection(db, ATTENDANCE_COLLECTION), where('date', '==', date));
  return onSnapshot(q, (snapshot) => {
    const records = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AttendanceRecord));
    callback(records);
  }, (error) => {
    console.error("Error subscribing to attendance", error);
    handleFirestoreError(error, OperationType.LIST, ATTENDANCE_COLLECTION);
  });
};

export const markAttendance = async (
  date: string, 
  studentId: string, 
  studentName: string, 
  type: 'IN' | 'OUT', 
  time: string,
  isStaff?: boolean,
  staffRole?: string
): Promise<void> => {
  try {
    const docId = `${date}_${studentId}`;
    const docRef = doc(db, ATTENDANCE_COLLECTION, docId);
    
    if (type === 'IN') {
      await setDoc(docRef, {
        id: docId,
        date,
        studentId,
        studentName,
        inTime: time,
        status: 'PRESENT',
        ...(isStaff !== undefined ? { isStaff } : {}),
        ...(staffRole !== undefined ? { staffRole } : {})
      }, { merge: true });
    } else if (type === 'OUT') {
      await setDoc(docRef, {
        id: docId,
        date,
        studentId,
        studentName,
        outTime: time,
        ...(isStaff !== undefined ? { isStaff } : {}),
        ...(staffRole !== undefined ? { staffRole } : {})
      }, { merge: true });
    }
  } catch (e) {
    console.error("Error marking attendance", e);
    throw e;
  }
};

export const clearAttendance = async (date: string, studentId: string): Promise<void> => {
  try {
    const docId = `${date}_${studentId}`;
    await deleteDoc(doc(db, ATTENDANCE_COLLECTION, docId));

    // Clear any legacy randomized ID doc for compatibility
    const q = query(collection(db, ATTENDANCE_COLLECTION), where('date', '==', date), where('studentId', '==', studentId));
    const snapshot = await getDocs(q);
    for (const docSnap of snapshot.docs) {
      if (docSnap.id !== docId) {
        await deleteDoc(doc(db, ATTENDANCE_COLLECTION, docSnap.id));
      }
    }
  } catch (e) {
    console.error("Error clearing attendance", e);
    throw e;
  }
};

export const getMergedRepeaterMembers = (survey: any, questions: any[]): any[] => {
  const arrayKeys = Object.keys(survey).filter(key => Array.isArray((survey as any)[key]));
  if (arrayKeys.length === 0) return [];

  let nameArrayKey = '';
  for (const key of arrayKeys) {
    const arr = survey[key] || [];
    if (arr.some((m: any) => getMemberName(m, questions))) {
      nameArrayKey = key;
      break;
    }
  }

  if (!nameArrayKey) {
    let fallback: any[] = [];
    for (const key of arrayKeys) {
      fallback = [...fallback, ...(survey[key] || [])];
    }
    return fallback;
  }

  const mainArr = survey[nameArrayKey] || [];
  return mainArr.map((member: any, idx: number) => {
    let mergedMember = { ...member };
    for (const key of arrayKeys) {
      if (key === nameArrayKey) continue;
      const otherArr = survey[key] || [];
      if (otherArr[idx] && typeof otherArr[idx] === 'object') {
        mergedMember = { ...mergedMember, ...otherArr[idx] };
      }
    }
    return mergedMember;
  });
};

export const extractStudentsFromSurvey = (survey: SurveyData, questions: any[]): any[] => {
  let allStudents: any[] = [];
  
  const isAdultOrNonStudent = (member: any): boolean => {
    if (!member || typeof member !== 'object') return true;
    
    // If they already have an assigned student serial number, they are definitely a student!
    const mSerial = String(member.serialNo || member.idNo || member.studentSerialNo || member.studentIdNo || '').toUpperCase();
    if (mSerial.startsWith('LS-') || mSerial.startsWith('CC-') || mSerial.startsWith('FL-')) {
      return false;
    }
    
    // 1. Strict student age category check (ages 4 to 21) if age is provided
    const age = getMemberAge(member, questions);
    if (!isNaN(age)) {
      const category = getCategoryForAge(age);
      if (category === null) {
        return true; // Outside student categories (e.g., adults/non-students)
      } else {
        return false; // Confirmed student age category (ages 4 to 21)!
      }
    }

    // 2. Check relationship and adult/parent/spouse terms in any value
    for (const key of Object.keys(member)) {
      const val = member[key];
      if (typeof val === 'string' && val.trim().length > 0) {
        const v = val.toLowerCase().trim();
        const isParentOrSpouseWord = 
          v.includes('father') || v.includes('mother') || v.includes('parent') || v.includes('guardian') ||
          v.includes('spouse') || v.includes('wife') || v.includes('husband') || v.includes('self') || v.includes('head') ||
          v.includes('പിതാവ്') || v.includes('പിതാ') || v.includes('അച്ഛൻ') || v.includes('ഉപ്പ') ||
          v.includes('മാതാവ്') || v.includes('മാതാ') || v.includes('അമ്മ') || v.includes('ഉമ്മ') ||
          v.includes('ഭാര്യ') || v.includes('ഭർത്താവ്') || v.includes('രക്ഷിതാവ്') || v.includes('രക്ഷിതാ') ||
          v.includes('രക്ഷിതാവിന്റെ') || v.includes('നാഥൻ') || v.includes('നാഥ') || v.includes('സ്വയം');
          
        if (isParentOrSpouseWord) {
          const k = key.toLowerCase();
          if (
            k.includes('relation') || k.includes('ബന്ധം') || k.includes('head') || 
            k.includes('status') || k.includes('role') || k.includes('type') ||
            v === 'father' || v === 'mother' || v === 'spouse' || v === 'wife' || v === 'husband' || v === 'self' || v === 'head' ||
            v === 'പിതാവ്' || v === 'മാതാവ്' || v === 'ഭാര്യ' || v === 'ഭർത്താവ്' || v === 'സ്വയം' || v === 'നാഥൻ'
          ) {
            return true;
          }
        }
      }
    }
    return false;
  };

  const getSurveyField = (sObj: any, fieldKeys: string[]): string => {
    if (!sObj) return '';
    for (const key of fieldKeys) {
      if (sObj[key] !== undefined && sObj[key] !== null) return String(sObj[key]).trim();
    }
    if (sObj.sectionA) {
      for (const key of fieldKeys) {
        if (sObj.sectionA[key] !== undefined && sObj.sectionA[key] !== null) return String(sObj.sectionA[key]).trim();
      }
    }
    for (const secKey of Object.keys(sObj)) {
      if (['id', 'submittedBy', 'submittedAt', 'paymentStatus', 'paymentAmount', 'paidBy', 'paidAt', 'sectionA'].includes(secKey)) continue;
      const section = sObj[secKey];
      if (section && typeof section === 'object' && !Array.isArray(section)) {
        for (const key of fieldKeys) {
          if (section[key] !== undefined && section[key] !== null) return String(section[key]).trim();
        }
      }
    }
    return '';
  };

  const flatData = Object.keys(survey).reduce((acc: any, key) => {
    if (['id', 'submittedBy', 'submittedAt', 'paymentStatus', 'paymentAmount', 'paidBy', 'paidAt'].includes(key)) return acc;
    const val = (survey as any)[key];
    if (Array.isArray(val)) return acc;
    if (typeof val === 'object' && val !== null) {
      return { ...acc, ...val };
    }
    return { ...acc, [key]: val };
  }, {});

  // Robust student name extraction (ignoring parent names)
  let studentName = '';
  const priorityStudentKeys = ['studentName', 'student_name', 'childName', '1782312338318'];
  for (const key of priorityStudentKeys) {
    if (flatData[key] && typeof flatData[key] === 'string' && flatData[key].trim().length > 0) {
      studentName = String(flatData[key]).trim();
      break;
    }
  }

  if (!studentName && questions && Array.isArray(questions)) {
    const studentNameQ = questions.find(q => {
      const labelLower = String(q.label || '').toLowerCase();
      const idLower = String(q.id || '').toLowerCase();
      return (
        (labelLower.includes('student') || labelLower.includes('child') || labelLower.includes('കുട്ടി') || labelLower.includes('വിദ്യാർത്ഥി')) &&
        (labelLower.includes('name') || labelLower.includes('പേര്'))
      );
    });
    if (studentNameQ && flatData[studentNameQ.id]) {
      studentName = String(flatData[studentNameQ.id]).trim();
    }
  }

  if (!studentName && questions && Array.isArray(questions)) {
    for (const q of questions) {
      const val = flatData[q.id];
      if (val) {
        const labelLower = String(q.label || '').toLowerCase();
        const idLower = String(q.id).toLowerCase();
        
        const isParentQ = 
          labelLower.includes('father') || labelLower.includes('mother') || labelLower.includes('parent') || labelLower.includes('guardian') ||
          labelLower.includes('പിതാവ്') || labelLower.includes('മാതാവ്') || labelLower.includes('രക്ഷിതാവ്') ||
          idLower.includes('father') || idLower.includes('mother') || idLower.includes('parent') || idLower.includes('guardian');
          
        if (!isParentQ && (labelLower.includes('name') || labelLower.includes('പേര്') || idLower.includes('name'))) {
          studentName = String(val).trim();
          break;
        }
      }
    }
  }

  if (!studentName) {
    studentName = getMemberName(flatData, questions);
  }
  
  // Extract parent fields to attach to students
  const fatherPhone = getSurveyField(survey, ['1782311944131', 'fatherPhone', 'father_phone', 'fatherContact', 'father_contact', 'phone', 'mobile']) || flatData['1782311944131'] || '';
  const motherPhone = getSurveyField(survey, ['1782311987788', 'motherPhone', 'mother_phone', 'motherContact', 'mother_contact']) || flatData['1782311987788'] || '';
  const fatherName = getSurveyField(survey, ['1782311895371', 'fatherName', 'father_name', 'father']) || flatData['1782311895371'] || '';
  const motherName = getSurveyField(survey, ['1782311923394', 'motherName', 'mother_name', 'mother']) || flatData['1782311923394'] || '';
  const hasMembership = flatData['1782312069160'];
  const membershipId = flatData['1782312123373'] || '';

  const repeaterMembers = getMergedRepeaterMembers(survey, questions);

  const primaryAge = getMemberAge(flatData, questions);
  const primaryStudentSerial = survey.sectionA?.studentSerialNo || survey.studentSerialNo || flatData.studentSerialNo || '';
  const isSerialCategoryLS = primaryStudentSerial.toUpperCase().startsWith('LS-');
  const isSerialCategoryCC = primaryStudentSerial.toUpperCase().startsWith('CC-');
  const isSerialCategoryFL = primaryStudentSerial.toUpperCase().startsWith('FL-');
  
  const primaryCategory = getCategoryForAge(primaryAge) || (isSerialCategoryLS ? 'LS' : isSerialCategoryCC ? 'CC' : isSerialCategoryFL ? 'FL' : null);
  const primaryNameLower = studentName ? studentName.toLowerCase() : '';
  const primaryIsHobbyWord = primaryNameLower.includes('hobby') || primaryNameLower.includes('hobbies') || primaryNameLower.includes('വിനോദം') || primaryNameLower.includes('sports') || primaryNameLower.includes('art') || primaryNameLower.includes('football') || primaryNameLower.includes('cricket') || primaryNameLower.includes('reading') || primaryNameLower.includes('drawing');
  const isPrimaryHobby = primaryIsHobbyWord && !primaryNameLower.includes('name') && !primaryNameLower.includes('പേര്');

  if (studentName && !isPrimaryHobby && primaryCategory !== null) {
    const locVal = getHouseholdLocation(survey, questions);
    allStudents.push({
      ...flatData,
      id: survey.id + '-main',
      studentName: studentName,
      serialNo: primaryStudentSerial || survey.sectionA?.serialNo || survey.surveyNo || survey.idNo || flatData.serialNo || flatData.idNo || 'PND',
      parentName: getHouseholdName(survey, questions),
      phone: getHouseholdPhone(survey, questions),
      location: locVal,
      '1782312786621': locVal,
      fatherPhone,
      motherPhone,
      fatherName,
      motherName,
      hasMembership,
      membershipId,
      surveyId: survey.id
    });
  }

  repeaterMembers.forEach((m: any, idx: number) => {
    const name = getMemberName(m, questions);
    if (name) {
      const age = getMemberAge(m, questions);
      const mSerial = m.serialNo || m.idNo || '';
      const isMSerialLS = mSerial.toUpperCase().startsWith('LS-');
      const isMSerialCC = mSerial.toUpperCase().startsWith('CC-');
      const isMSerialFL = mSerial.toUpperCase().startsWith('FL-');
      const category = getCategoryForAge(age) || (isMSerialLS ? 'LS' : isMSerialCC ? 'CC' : isMSerialFL ? 'FL' : null);
      if (category === null) return;
      if (isAdultOrNonStudent(m)) return;
      if (studentName && name && studentName.toLowerCase().trim() === name.toLowerCase().trim()) return;
      
      const nameLower = name.toLowerCase();
      const isHobbyWord = nameLower.includes('hobby') || nameLower.includes('hobbies') || nameLower.includes('വിനോദം') || nameLower.includes('sports') || nameLower.includes('art') || nameLower.includes('football') || nameLower.includes('cricket') || nameLower.includes('reading') || nameLower.includes('drawing');
      if (isHobbyWord && !nameLower.includes('name') && !nameLower.includes('പേര്')) return;

      const locVal = getHouseholdLocation(survey, questions);
      allStudents.push({
        ...flatData,
        ...m,
        id: survey.id + '-rep-' + idx,
        studentName: name,
        serialNo: m.serialNo || m.idNo || survey.sectionA?.serialNo || survey.surveyNo || survey.serialNo || flatData.serialNo || 'PND',
        parentName: getHouseholdName(survey, questions),
        phone: getHouseholdPhone(survey, questions),
        location: locVal,
        '1782312786621': locVal,
        fatherPhone,
        motherPhone,
        fatherName,
        motherName,
        hasMembership,
        membershipId,
        surveyId: survey.id
      });
    }
  });

  const uniqueStudents: any[] = [];
  allStudents.forEach(s => {
    const isDuplicate = uniqueStudents.some(u => {
      const nameMatch = (s.studentName && u.studentName && s.studentName.toLowerCase().trim() === u.studentName.toLowerCase().trim());
      const serialMatch = (s.serialNo && u.serialNo && String(s.serialNo).trim() !== 'PND' && String(s.serialNo).trim() === String(u.serialNo).trim());
      return nameMatch || serialMatch;
    });
    if (!isDuplicate) {
      uniqueStudents.push(s);
    }
  });

  return uniqueStudents;
};

const STAFF_COLLECTION = 'staff';

export const saveStaff = async (staff: StaffMember): Promise<StaffMember> => {
  try {
    await setDoc(doc(db, STAFF_COLLECTION, staff.id), staff);
    return staff;
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, `${STAFF_COLLECTION}/${staff.id}`);
    throw error;
  }
};

export const getStaff = async (): Promise<StaffMember[]> => {
  try {
    const snapshot = await getDocs(collection(db, STAFF_COLLECTION));
    return snapshot.docs.map(d => d.data() as StaffMember);
  } catch (error) {
    console.error("Failed to fetch staff:", error);
    handleFirestoreError(error, OperationType.LIST, STAFF_COLLECTION);
    return [];
  }
};

export const deleteStaff = async (id: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, STAFF_COLLECTION, id));
  } catch (error) {
    handleFirestoreError(error, OperationType.DELETE, `${STAFF_COLLECTION}/${id}`);
    throw error;
  }
};

export const getNextStaffSerialNo = async (role: 'faculty' | 'coordinator'): Promise<string> => {
  try {
    const metaRef = doc(db, SETTINGS_COLLECTION, 'metadata');
    const fieldKey = role === 'faculty' ? 'lastFacultyNo' : 'lastCoordinatorNo';
    const prefix = role === 'faculty' ? 'FAC' : 'COOR';

    // 1. Atomically increment the counter to ensure concurrency safety
    await setDoc(metaRef, { [fieldKey]: increment(1) }, { merge: true });

    // 2. Fetch the updated document to retrieve the incremented value
    const metaSnap = await getDoc(metaRef);
    
    let nextNo = 1;
    if (metaSnap.exists() && typeof metaSnap.data()[fieldKey] === 'number') {
      nextNo = metaSnap.data()[fieldKey];
    } else {
      // emergency fallback / scanner
      const staffList = await getStaff();
      const roleStaff = staffList.filter(s => s.role === role);
      let lastNo = 0;
      roleStaff.forEach(s => {
        const numStr = s.serialNo.replace(`${prefix}-`, '');
        const num = parseInt(numStr, 10);
        if (!isNaN(num) && num > lastNo) lastNo = num;
      });
      nextNo = lastNo + 1;
      // sync back the corrected number
      await setDoc(metaRef, { [fieldKey]: nextNo }, { merge: true });
    }
    
    return `${prefix}-${nextNo.toString().padStart(4, '0')}`;
  } catch (e) {
    console.error("Failed to generate staff serial:", e);
    const rand = Math.floor(1000 + Math.random() * 9000);
    return role === 'faculty' ? `FAC-${rand}` : `COOR-${rand}`;
  }
};

export const saveTemplateByType = async (type: string, template: CardTemplate): Promise<void> => {
  const dataToSave = { ...template };
  if (dataToSave.photoConfig === undefined) {
    dataToSave.photoConfig = null;
  }
  await setDoc(doc(db, SETTINGS_COLLECTION, `template_${type}`), dataToSave);
};

export const getTemplateByType = async (type: string): Promise<CardTemplate | null> => {
  try {
    if (type === 'student') {
      const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'template_student'));
      if (docSnap.exists()) return docSnap.data() as CardTemplate;
      
      const legacySnap = await getDoc(doc(db, SETTINGS_COLLECTION, 'template'));
      return legacySnap.exists() ? legacySnap.data() as CardTemplate : null;
    }
    
    const docSnap = await getDoc(doc(db, SETTINGS_COLLECTION, `template_${type}`));
    return docSnap.exists() ? docSnap.data() as CardTemplate : null;
  } catch (e) {
    console.warn(`Failed to get template for ${type}:`, e);
    return null;
  }
};
